import React, { useState, useContext, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import Spinner from "../../../components/Spinner";
import {
	checkConnectionStatus,
	verifyHideCountryItems,
	COUNTRY_CHANGED_EVENT,
} from "../../../utils/Common";
// auth
import AuthContext from "../../../contexts/auth";
import SidebarContext from "../../../contexts/sidebar";
import { translate } from "../../../utils/Common";
import CountryList from "../../../components/Country";
import AuthShell, { AuthHeading } from "../../../components/layout/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";

// Minimal field skin: a soft fill instead of an outline, with the border and a
// wide focus ring appearing only on focus.
const FIELD =
	"h-12 rounded-xl border-transparent bg-muted/60 px-4 text-[15px] shadow-none " +
	"placeholder:text-muted-foreground/70 focus-visible:border-primary focus-visible:bg-background " +
	"focus-visible:ring-4 focus-visible:ring-primary/15 focus-visible:ring-offset-0";

const SignIn = () => {
	const userContext = useContext(AuthContext);
	const sidebarContext = useContext(SidebarContext);
	const history = useHistory();
	const { register, handleSubmit, errors, reset, setError } = useForm();
	const [isServerRun, setIsServerRun] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState();
	const [showPassword, setShowPassword] = useState(false);
	// `verifyHideCountryItems` reads localStorage directly, so switching country
	// has to nudge a re-render for the sign-up link to appear or disappear.
	// This is what the removed page reload used to achieve.
	const [, setCountryTick] = useState(0);

	useEffect(() => {
		const onCountryChanged = () => setCountryTick((tick) => tick + 1);
		window.addEventListener(COUNTRY_CHANGED_EVENT, onCountryChanged);
		return () => window.removeEventListener(COUNTRY_CHANGED_EVENT, onCountryChanged);
	}, []);

	const onSubmit = async (data) => {
		setMessage("");
		setIsServerRun(false);
		setIsLoading(true);
		const result = await userContext.signIn(data);
		setIsLoading(false);

		// The form disables itself while the request is in flight, so every
		// outcome other than a successful sign-in has to hand it back. It did
		// not: `SERVER_ERROR` explicitly left it disabled, and any result the
		// switch below does not know about fell through `default` and left it
		// disabled too. Either way the user could not retry — the fields and
		// the button stayed dead until the page was reloaded, which is exactly
		// what happened whenever the API was down.
		if (result !== "LOGIN_SUCCESS") setIsServerRun(true);

		switch (result) {
			case "LOGIN_SUCCESS":
				reset();
				sidebarContext.handleChangeCurrentPage(
					translate("sidebar.options.start"),
				);
				history.push("/dashboard");
				break;
			case "PASSWORD_INVALID":
				setError("pwd", {
					type: "",
					message: translate("login.signin.validate.passIncorrect"),
				});
				break;
			case "USER_NOT_FOUND":
				setError("username", {
					type: "",
					message: translate("login.signin.validate.userNotFound"),
				});
				break;
			case "EMAIL_NOT_FOUND":
				setError("username", {
					type: "",
					message: translate("login.signin.validate.emailNotFound"),
				});
				break;
			case "USER_INACTIVATED":
				setError("username", {
					type: "",
					message: translate("login.signin.validate.userInactive"),
				});
				break;
			case "USER_NOT_VERIFIED":
				setError("username", {
					type: "",
					message: translate("login.signin.validate.userNotVerified"),
				});
				break;
			// An unrecognised result means the server answered something we
			// cannot act on, which for the user is the same as the server being
			// down. Say so, instead of failing silently.
			case "SERVER_ERROR":
			default:
				if (checkConnectionStatus()) {
					setMessage(translate("login.signin.validate.errorConnectionTrue"));
				} else {
					setMessage(translate("login.signin.validate.errorConnectionFalse"));
				}
				break;
		}
	};

	return (
		<AuthShell>
			<AuthHeading title={translate("login.signin.title")}>
				<CountryList />
			</AuthHeading>

			<form className="mt-8 space-y-5" onSubmit={handleSubmit(onSubmit)}>
				<div className="space-y-2">
					<Label htmlFor="username" className="text-[13px] text-muted-foreground">
						{translate("login.signin.user")}
					</Label>
					<Input
						id="username"
						name="username"
						type="text"
						autoComplete="username"
						disabled={!isServerRun}
						required
						autoFocus
						aria-invalid={errors.username ? "true" : "false"}
						className={FIELD}
						placeholder={translate("login.signin.user")}
						ref={register({
							minLength: {
								value: 6,
								message: translate("login.signin.validate.minLength"),
							},
							maxLength: {
								value: 20,
								message: translate("login.signin.validate.maxLength"),
							},
						})}
					/>
					{errors.username && (
						<p className="text-xs font-medium text-destructive">
							{errors.username.message}
						</p>
					)}
				</div>

				<div className="space-y-2">
					<Label htmlFor="pwd" className="text-[13px] text-muted-foreground">
						{translate("login.signin.password")}
					</Label>
					<div className="relative">
						<Input
							id="pwd"
							name="pwd"
							type={showPassword ? "text" : "password"}
							autoComplete="current-password"
							disabled={!isServerRun}
							required
							aria-invalid={errors.pwd ? "true" : "false"}
							className={`${FIELD} pr-12`}
							placeholder={translate("login.signin.password")}
							ref={register({
								minLength: {
									value: 6,
									message: translate("login.resetpwd.validate.minLength"),
								},
								maxLength: {
									value: 16,
									message: translate("login.resetpwd.validate.maxLength"),
								},
							})}
						/>
						{/* Typing a password blind is the most common cause of a failed
						    login attempt; this was missing entirely. */}
						<button
							type="button"
							tabIndex={-1}
							onClick={() => setShowPassword((v) => !v)}
							aria-label={translate(showPassword ? "login.signin.hidePassword" : "login.signin.showPassword")}
							className="absolute right-1.5 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-lg border-0 bg-transparent text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						>
							{showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
						</button>
					</div>
					{errors.pwd && (
						<p className="text-xs font-medium text-destructive">
							{errors.pwd.message}
						</p>
					)}
				</div>

				<div className="flex justify-end">
					<Link
						to="/forgotpwd"
						className="text-xs font-medium text-primary-strong transition-colors hover:text-secondary hover:underline"
					>
						{translate("login.signin.forgot")}
					</Link>
				</div>

				<Button
					type="submit"
					size="lg"
					disabled={!isServerRun}
					className="w-full"
				>
					{!isLoading ? (
						translate("login.signin.button")
					) : (
						<Spinner type={1} size={18} />
					)}
				</Button>
			</form>

			{message && (
				<p
					role="alert"
					className="mt-4 rounded-md bg-destructive/10 px-3 py-2 text-center text-xs font-medium text-destructive"
				>
					{message}
				</p>
			)}

			{verifyHideCountryItems(["br", "us"]) && (
				<p className="mt-6 text-center text-sm text-muted-foreground">
					{translate("login.signin.question")}{" "}
					<Link
						to="/registrar"
						className="font-semibold text-primary-strong transition-colors hover:text-secondary hover:underline"
					>
						{translate("login.signin.registerHere")}
					</Link>
				</p>
			)}
		</AuthShell>
	);
};

export default SignIn;
